// The instrument abstraction (docs/05 §5.6). Live play and loop replay both
// call trigger(), so what you record is what you hear.

import type * as Tone from "tone";
import type { NoteEvent, ScaleName } from "../util/types.ts";

export type InstrumentCategory =
  | "drums"
  | "perc"
  | "bass"
  | "keys"
  | "pads"
  | "strings"
  | "plucked"
  | "leads"
  | "wind";
export type PlayArchetype = "air-drum" | "theremin" | "air-keys" | "strum" | "shake";

export interface Instrument {
  readonly id: string;
  readonly name: string;
  readonly category: InstrumentCategory;
  readonly archetype: PlayArchetype;
  readonly scaleLocked: boolean;
  readonly polyphony: "mono" | "poly" | number;

  /** Build the Tone graph and connect its output to the provided node. */
  init(output: Tone.ToneAudioNode): void;

  /** Play a note now (time omitted) or at a scheduled transport time. */
  trigger(ev: NoteEvent, time?: Tone.Unit.Time): void;

  /** Start a sustained note (live theremin/pad hold). */
  attack?(ev: NoteEvent, time?: Tone.Unit.Time): void;

  /** Release a sustained note started with attack(). */
  release?(ev: NoteEvent, time?: Tone.Unit.Time): void;

  /** Set the key/scale used to resolve degree events (scale-locked only). */
  setScale?(key: string, scale: ScaleName): void;

  /** Continuous expression macro 0..1 (filter/vol/vibrato), from the left hand. */
  setMacro?(value: number): void;

  dispose(): void;
}

export function clampVelocity(v: number): number {
  return Math.max(0.05, Math.min(1, v));
}

export function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

/** Map a 0..1 macro to a musically useful filter cutoff (Hz, exponential). */
export function macroToCutoff(v: number): number {
  return 300 * Math.pow(8000 / 300, clamp01(v));
}
